import ReactFlow, {
  Background,
  Controls,
  MarkerType,
  Position,
  type Edge,
  type Node,
  type NodeMouseHandler,
} from 'reactflow';
import 'reactflow/dist/style.css';
import dagre from '@dagrejs/dagre';

/**
 * The memory bank's drawing surface — the explore half of the memory section
 * (`OrgMemoryBankSection`). Ported from ROC's sqlite-cubby canvas (same node
 * geometry, same dagre flow), restyled onto this app's conventions: the
 * orgs family's DS tokens instead of ROC's MUI sx styling.
 *
 * The node's FILL is its `privacy`, which is the entire point of the view: a
 * caller sees the subgraph their grants reach, so the restricted nodes are
 * the visible difference between what an owner and a member see. That
 * mapping lives HERE once so the canvas, its legend, and the detail pane's
 * privacy chip cannot drift into coloring privacy differently.
 *
 * Tier 2 adds two VISUAL-ONLY layers on top of that tint, both passed in by
 * the section: node `opacity` (recency quartile, or focus mode's dim — the
 * tint modulates, it never changes) and the hub `+N more` chip node (the
 * surplus of a node's loaded-visible degree collapsed into one affordance).
 */

const NODE_W = 180;
const NODE_H = 44;

/**
 * Privacy → fill/stroke, least to most restricted; the restricted end is the
 * one that must be unmistakable at a glance. DS tokens only: blue = public,
 * violet = internal, orange = private, red = restricted.
 */
export const PRIVACY_COLORS: Record<string, { fill: string; stroke: string }> = {
  public: { fill: 'var(--c-blue-light)', stroke: 'var(--c-blue-dark)' },
  internal: { fill: 'var(--c-violet-light)', stroke: 'var(--c-violet-dark)' },
  private: { fill: 'var(--c-orange-light)', stroke: 'var(--c-orange-dark)' },
  restricted: { fill: 'var(--c-red-light)', stroke: 'var(--c-red-dark)' },
};
const UNKNOWN_COLORS = { fill: 'var(--surface-low)', stroke: 'var(--stroke-on-med)' };

export function privacyColors(privacy: string | null | undefined): { fill: string; stroke: string } {
  return (privacy != null && PRIVACY_COLORS[privacy]) || UNKNOWN_COLORS;
}

/** A record as the section knows it for drawing (the `record_list` and
 *  `record_neighbours` projections, mapped). `updatedAt` is optional by
 *  projection: the feed carries it, the neighbours rows do not — a node
 *  without one takes no part in the recency encoding (neutral opacity). */
export interface MemoryGraphNodeData {
  id: string;
  title: string | null;
  type: string | null;
  scope: string | null;
  privacy: string | null;
  updatedAt?: string | null;
}

export function layout(nodes: Node[], edges: Edge[]): Node[] {
  const g = new dagre.graphlib.Graph();
  // Left-to-right: the demo's `answered` chain reads as a timeline — ask on
  // the left, answer one rank right, hop by hop.
  g.setGraph({ rankdir: 'LR', nodesep: 24, ranksep: 90 });
  g.setDefaultEdgeLabel(() => ({}));
  nodes.forEach((n) => g.setNode(n.id, { width: NODE_W, height: NODE_H }));
  // dagre throws on an edge naming a node it does not have; callers must
  // hand over only edges whose endpoints they also hold as nodes.
  edges.forEach((e) => g.setEdge(e.source, e.target));
  dagre.layout(g);
  return nodes.map((n) => {
    const p = g.node(n.id);
    return { ...n, position: { x: p.x - NODE_W / 2, y: p.y - NODE_H / 2 } };
  });
}

/** The extra visuals a node may carry, all OPTIONAL additions on top of the
 *  privacy tint (which stays the contract):
 *  - `opacity` — the recency encoding's quartile opacity, or focus mode's
 *    dim. `undefined` means "no encoding" (no known timestamp, focus off):
 *    the style key stays ABSENT rather than being pinned to 1, so a test (or
 *    a reader) can tell "encoded fresh" from "not encoded at all".
 *  - `onMenu` — renders the node's ⋯ (Advanced Expansion) affordance. */
export interface MemoryNodeVisuals {
  opacity?: number;
  onMenu?: (id: string) => void;
}

export function memoryNode(
  rec: MemoryGraphNodeData,
  selected: boolean,
  matched = false,
  visuals: MemoryNodeVisuals = {},
): Node {
  const colors = privacyColors(rec.privacy);
  return {
    id: rec.id,
    data: {
      label: (
        <div className="vault-orgs__memory-node-label">
          <span className="vault-orgs__memory-node-title">{rec.title || rec.id}</span>
          <span className="vault-orgs__memory-node-caption">
            {[rec.type, rec.scope].filter(Boolean).join(' · ')}
          </span>
          {visuals.onMenu !== undefined && (
            <button
              type="button"
              className="vault-orgs__memory-node-menu"
              aria-label={`Expansion options for ${rec.title || rec.id}`}
              data-testid={`memory-node-menu-${rec.id}`}
              // The kebab is the node's CONTEXT menu, not a selection: stop
              // the click before reactflow's node-click selects the node.
              onClick={(e) => { e.stopPropagation(); visuals.onMenu!(rec.id); }}
            >
              ⋯
            </button>
          )}
        </div>
      ),
    },
    position: { x: 0, y: 0 },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    selected,
    // Colors inline, not via class: reactflow's own node stylesheet would
    // otherwise outrank a same-specificity class and silently repaint nodes.
    style: {
      width: NODE_W,
      height: NODE_H,
      background: colors.fill,
      border: `1.5px solid ${colors.stroke}`,
      borderRadius: 6,
      padding: 0,
      fontSize: 11,
      // The selection ring the canvas cannot leave to reactflow's `.selected`
      // styling — the pane and the ring must never disagree. A MATCHED node
      // (one the panel's current search returns) carries a thinner blue ring
      // instead — search↔graph sync, tier 1. Both rings are ADDITIVE
      // boxShadow decoration: the fill/stroke privacy tint above is the
      // contract and neither ring may touch it. Selection wins when both
      // apply, so the pane's record stays unambiguous.
      boxShadow: selected
        ? '0 0 0 2px var(--c-violet)'
        : matched
          ? '0 0 0 2px var(--c-blue-dark)'
          : undefined,
      ...(visuals.opacity !== undefined ? { opacity: visuals.opacity } : {}),
    },
  };
}

/** The synthetic id prefix of a hub's `+N more` chip node. Prefixed so it can
 *  never collide with a record id, and so the click handler can tell a chip
 *  (fetch the next batch) from a record (select) by inspection. */
export const HUB_NODE_PREFIX = '__hub-more:';

/**
 * A hub's `+N more` chip (Bloom's node-limit pattern): the surplus of one
 * node's VISIBLE degree, collapsed into ONE affordance — never a starburst.
 * NOT a record: no privacy, no selection, no counting toward the record
 * readout. `moreCount` is LOADED-VISIBLE surplus only — edges the viewer's
 * own reads returned and the graph actually holds; it is never a server-side
 * degree and never a guess about hidden relations.
 */
export function hubChipNode(hubId: string, moreCount: number, opacity?: number): Node {
  return {
    id: `${HUB_NODE_PREFIX}${hubId}`,
    data: {
      label: (
        <div className="vault-orgs__memory-node-label vault-orgs__memory-node-label--hub">
          <span className="vault-orgs__memory-node-title">{`+${moreCount} more`}</span>
        </div>
      ),
    },
    position: { x: 0, y: 0 },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    selected: false,
    style: {
      width: NODE_W,
      height: 34,
      background: 'var(--surface-low)',
      // Dashed: the chip is an affordance, visually distinct from every
      // privacy tint so it can never be misread as a record class.
      border: '1.5px dashed var(--text-muted)',
      borderRadius: 999,
      padding: 0,
      fontSize: 11,
      ...(opacity !== undefined ? { opacity } : {}),
    },
  };
}

export function memoryEdge(
  source: string,
  target: string,
  label: string | null,
  index: number,
  dimmed = false,
): Edge {
  return {
    id: `${source}->${target}#${index}`,
    source,
    target,
    // The EDGE's type (record_neighbours' second `type` column) is the one
    // thing worth writing on the wire: `answered`, `cites`, …
    label: label ?? undefined,
    labelStyle: { fontSize: 9 },
    style: { stroke: 'var(--text-muted)', ...(dimmed ? { opacity: 0.25 } : {}) },
    markerEnd: { type: MarkerType.ArrowClosed, color: 'var(--text-muted)' },
  };
}

/** The result of the hub-grouping pass: which real nodes render, which
 *  edges stay drawn, and one chip per hub carrying its surplus count. */
export interface GroupedHubs {
  visible: MemoryGraphNodeData[];
  keptEdges: Array<{ source: string; target: string; label: string | null }>;
  hubs: Array<{ hubId: string; moreCount: number }>;
}

/**
 * The hub-grouping pass (tier 2, Bloom's node-limit pattern): cap every
 * node's DRAWN degree at its threshold and collapse the surplus behind a
 * `+N more` chip — never a starburst. Edges are walked in the caller's
 * (insertion) order, so the cut is deterministic: a node's first
 * threshold-many relations draw, later ones join its chip. An edge that
 * would push EITHER endpoint over the endpoint's threshold groups under the
 * endpoint already at it (the source wins if both are — a fixed rule, not a
 * judgement).
 *
 * `extra` is the per-hub REVEALED allowance (what the hub's chip clicks have
 * unlocked, in edges): a hub whose chip was clicked draws
 * `threshold + extra[hub]` edges before grouping, which is what makes the
 * chip's "fetch next batch" a reveal rather than a re-fetch that changes
 * nothing.
 *
 * GUARDRAIL: `moreCount` is LOADED-VISIBLE surplus only — edges the
 * viewer's own reads returned and the caller's graph actually holds. It is
 * not a server-side degree and carries no claim about relations the gate
 * hides. A node whose every edge grouped away does not render (it rides its
 * neighbour's chip); a node with NO edges renders (it is a seed).
 */
export function groupHubs(
  nodes: Iterable<MemoryGraphNodeData>,
  edges: Iterable<{ source: string; target: string; label: string | null }>,
  threshold: number,
  extra: ReadonlyMap<string, number> = new Map(),
): GroupedHubs {
  const nodeArr = [...nodes];
  const edgeArr = [...edges];
  const limitOf = (id: string): number => threshold + (extra.get(id) ?? 0);
  // Total incident edges per node — distinguishes "seed, nothing grouped"
  // from "everything this node had was grouped away".
  const incident = new Map<string, number>();
  for (const e of edgeArr) {
    incident.set(e.source, (incident.get(e.source) ?? 0) + 1);
    incident.set(e.target, (incident.get(e.target) ?? 0) + 1);
  }
  const drawnDegree = new Map<string, number>();
  const keptIncident = new Set<string>();
  const surplusByHub = new Map<string, number>();
  const keptEdges: GroupedHubs['keptEdges'] = [];
  for (const e of edgeArr) {
    const s = drawnDegree.get(e.source) ?? 0;
    const t = drawnDegree.get(e.target) ?? 0;
    if (s < limitOf(e.source) && t < limitOf(e.target)) {
      keptEdges.push(e);
      keptIncident.add(e.source);
      keptIncident.add(e.target);
      drawnDegree.set(e.source, s + 1);
      drawnDegree.set(e.target, t + 1);
    } else {
      const hub = s >= limitOf(e.source) ? e.source : e.target;
      surplusByHub.set(hub, (surplusByHub.get(hub) ?? 0) + 1);
    }
  }
  return {
    visible: nodeArr.filter((n) => (incident.get(n.id) ?? 0) === 0 || keptIncident.has(n.id)),
    keptEdges,
    hubs: [...surplusByHub.entries()].map(([hubId, moreCount]) => ({ hubId, moreCount })),
  };
}

/** The privacy key, small — a reader decodes a fill without hovering. */
export function MemoryPrivacyLegend() {
  return (
    <span className="vault-orgs__memory-legend" role="img" aria-label="Node fill by privacy">
      {Object.keys(PRIVACY_COLORS).map((p) => {
        const colors = PRIVACY_COLORS[p]!;
        return (
          <span
            key={p}
            className="vault-orgs__memory-legend-key"
            style={{ background: colors.fill, borderColor: colors.stroke }}
          >
            {p}
          </span>
        );
      })}
    </span>
  );
}

/**
 * Nodes + edges, laid out, drawn. Read-only surface: not draggable, not
 * connectable — this is a view of what the server disclosed, not an editor.
 * Node single-click selects (detail pane); double-click expands (reads the
 * node's neighbourhood) — the section owns both reads.
 */
export function MemoryGraphCanvas({ nodes, edges, onNodeClick, onNodeDoubleClick }: {
  nodes: Node[];
  edges: Edge[];
  onNodeClick?: NodeMouseHandler;
  onNodeDoubleClick?: NodeMouseHandler;
}) {
  return (
    <div className="vault-orgs__memory-canvas">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        fitView
        proOptions={{ hideAttribution: true }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable
        onNodeClick={onNodeClick}
        onNodeDoubleClick={onNodeDoubleClick}
      >
        <Background />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
}
